import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../shared/services/api.service';
import { catchError, forkJoin, map, of, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaterialModule } from '../../shared/theming/material/material.module';
import {
  EvaluationResult,
  EvaluationResponse,
  ComplianceFinding,
  ComplianceResponse,
  GROUPS,
  METRICS,
  EvaluationSource,
  TranscriptAnalysisRun,
  TranscriptAnalysisRunsResponse,
  EvaluationRun,
  EvaluationRunsResponse,
  ReviewStatus,
  UpdateReviewStatusRequest,
  UpdateReviewStatusResponse,
} from './models/quality.models';
import { EvaluationResultsWidgetComponent } from './components/evaluation-results-widget.component';
import { ComplianceFindingsWidgetComponent } from './components/compliance-findings-widget.component';
import { TranscriptAnalysisRunsWidgetComponent } from './components/transcript-analysis-runs-widget.component';
import { EvaluationRunsWidgetComponent } from './components/evaluation-runs-widget.component';
import { WeeklyAlertsWidgetComponent } from './components/weekly-alerts-widget.component';

interface ComplianceFilters {
  startDate: string;
  endDate: string;
  sk: string;
  limit: number;
}

@Component({
  selector: 'app-conversation-quality',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    EvaluationResultsWidgetComponent,
    ComplianceFindingsWidgetComponent,
    TranscriptAnalysisRunsWidgetComponent,
    EvaluationRunsWidgetComponent,
    WeeklyAlertsWidgetComponent,
    MaterialModule,
  ],
  templateUrl: './conversation-quality.component.html',
  styleUrls: ['./conversation-quality.component.scss'],
})
export class ConversationQualityComponent {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly snackBar = inject(MatSnackBar);
  readonly groups = GROUPS;
  readonly voiceGroups = ['white-goods'] as const;
  readonly metrics = METRICS;

  isResultsRoute(): boolean {
    return this.router.url.includes('evaluation-results');
  }

  goBackToMain(): void {
    this.router.navigate(['../'], {
      relativeTo: this.route.firstChild || this.route,
    });
  }
  agentEvaluationRuns: EvaluationRun[] = [];
  voiceEvaluationRuns: EvaluationRun[] = [];
  agentEvaluationRunsLoading = false;
  voiceEvaluationRunsLoading = false;
  agentEvaluationRunsError = '';
  voiceEvaluationRunsError = '';
  agentAnalysisRuns: TranscriptAnalysisRun[] = [];
  voiceAnalysisRuns: TranscriptAnalysisRun[] = [];
  agentRunsLoading = false;
  voiceRunsLoading = false;
  agentRunsError = '';
  voiceRunsError = '';
  agentAnalysisRunCount = 0;
  voiceAnalysisRunCount = 0;
  agentAnalysisRunDays = 7;
  voiceAnalysisRunDays = 7;
  agentRunDate = '';
  voiceRunDate = '';
  agentGroup = '';
  voiceGroup = '';
  agentMetric = '';
  voiceMetric = '';
  agentAverageQualityScore: number | null = null;
  voiceAverageQualityScore: number | null = null;
  agentResults: EvaluationResult[] = [];
  voiceResults: EvaluationResult[] = [];
  agentLoading = false;
  voiceLoading = false;
  agentError = '';
  voiceError = '';
  agentFindings: ComplianceFinding[] = [];
  voiceFindings: ComplianceFinding[] = [];
  updatingReviewKeys = new Set<string>();
  agentComplianceLoading = false;
  voiceComplianceLoading = false;
  agentComplianceError = '';
  agentWeeklyAlerts: ComplianceFinding[] = [];
  voiceWeeklyAlerts: ComplianceFinding[] = [];
  agentWeeklyLoading = false;
  voiceWeeklyLoading = false;
  agentWeeklyError = '';
  voiceWeeklyError = '';
  voiceComplianceError = '';
  private agentFilters: ComplianceFilters = {
    startDate: '',
    endDate: '',
    sk: '',
    limit: 50,
  };
  private voiceFilters: ComplianceFilters = {
    startDate: '',
    endDate: '',
    sk: '',
    limit: 50,
  };

  averageNumbers(values: number[]): number | null {
    return values.length
      ? values.reduce((sum, value) => sum + value, 0) / values.length
      : null;
  }

  averageScore(results: EvaluationResult[]): number | null {
    return this.averageNumbers(results.map((item) => item.score));
  }

  averageOfRunAverages(runAverages: number[]): number | null {
    return this.averageNumbers(runAverages);
  }

  constructor() {
    this.loadEvaluationRuns('agent-llm');
    this.loadEvaluationRuns('voice2voice');
    this.loadAnalysisRuns('agent-llm');
    this.loadAnalysisRuns('voice2voice');
    const scheduledRunDate = this.previousCompleteScheduledRunDate();
    this.agentRunDate = scheduledRunDate;
    this.voiceRunDate = scheduledRunDate;
    this.loadWeeklyAlerts('agent-llm', 0);
    this.loadWeeklyAlerts('voice2voice', 0);
    this.loadCompliance('agent-llm', this.agentFilters);
    this.loadCompliance('voice2voice', this.voiceFilters);
  }

  loadEvaluationRuns(source: EvaluationSource): void {
    if (source === 'agent-llm') {
      this.agentEvaluationRunsLoading = true;
      this.agentEvaluationRunsError = '';
    } else {
      this.voiceEvaluationRunsLoading = true;
      this.voiceEvaluationRunsError = '';
    }
    this.api
      .get<EvaluationRunsResponse>('admin/get-evaluation-runs', {
        source,
        days: '30',
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const runs = response.data ?? [];
          if (source === 'agent-llm') {
            this.agentEvaluationRuns = runs;
            this.agentEvaluationRunsLoading = false;
            this.loadAverageRunEvaluationScore(source, runs);
          } else {
            this.voiceEvaluationRuns = runs;
            this.voiceEvaluationRunsLoading = false;
            this.loadAverageRunEvaluationScore(source, runs);
          }
        },
        error: (error: Error) => {
          if (source === 'agent-llm') {
            this.agentEvaluationRunsError = this.errorMessage(error);
            this.agentEvaluationRunsLoading = false;
          } else {
            this.voiceEvaluationRunsError = this.errorMessage(error);
            this.voiceEvaluationRunsLoading = false;
          }
        },
      });
  }
  selectEvaluationRun(source: EvaluationSource, runId: string): void {
    const runDate = runId.match(/weekly-(\d{4}-\d{2}-\d{2})$/)?.[1] ?? '';
    if (source === 'agent-llm') this.agentRunDate = runDate;
    else this.voiceRunDate = runDate;

    // Redirect to the new child route representing results view
    this.router
      .navigate(['evaluation-results'], { relativeTo: this.route })
      .then(() => {
        this.snackBar.open(
          `Loading evaluation results for ${runId}`,
          'Dismiss',
          {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'bottom',
          }
        );
      });

    this.fetchEvaluationResults(source, runId);
  }

  loadWeeklyAlerts(source: EvaluationSource, weeksAgo: number): void {
    // Cumulative window ending today: weeksAgo 0 = last 7 days, 1 = last 2
    // weeks, 2 = last 3 weeks, 3 = last 4 weeks (kept in sync with
    // WeeklyAlertsWidgetComponent.calculateWeekBoundary).
    const now = new Date();
    const end = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        23,
        59,
        59,
        999
      )
    );
    const start = new Date(
      Date.UTC(
        end.getUTCFullYear(),
        end.getUTCMonth(),
        end.getUTCDate() - (weeksAgo + 1) * 7 + 1,
        0,
        0,
        0,
        0
      )
    );
    const startDate = start.toISOString();
    const endDate = end.toISOString();
    this.loadWeeklyAlertsWithRange(source, { startDate, endDate });
  }

  loadWeeklyAlertsWithRange(
    source: EvaluationSource,
    range: { startDate: string; endDate: string }
  ): void {
    if (source === 'agent-llm') {
      this.agentWeeklyLoading = true;
      this.agentWeeklyError = '';
    } else {
      this.voiceWeeklyLoading = true;
      this.voiceWeeklyError = '';
    }
    this.api
      .get<ComplianceResponse>('admin/get-transcript-analysis-alerts', {
        source,
        startDate: range.startDate,
        endDate: range.endDate,
        limit: '50',
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (source === 'agent-llm') {
            this.agentWeeklyAlerts = response.data ?? [];
            this.agentWeeklyLoading = false;
          } else {
            this.voiceWeeklyAlerts = response.data ?? [];
            this.voiceWeeklyLoading = false;
          }
        },
        error: (error: Error) => {
          if (source === 'agent-llm') {
            this.agentWeeklyError = this.errorMessage(error);
            this.agentWeeklyLoading = false;
          } else {
            this.voiceWeeklyError = this.errorMessage(error);
            this.voiceWeeklyLoading = false;
          }
        },
      });
  }

  loadAnalysisRuns(
    source: EvaluationSource,
    options: { days: number; limit: number } = { days: 7, limit: 50 }
  ): void {
    if (source === 'agent-llm') {
      this.agentRunsLoading = true;
      this.agentRunsError = '';
    } else {
      this.voiceRunsLoading = true;
      this.voiceRunsError = '';
    }
    this.api
      .get<TranscriptAnalysisRunsResponse>(
        'admin/get-transcript-analysis-runs',
        {
          source,
          days: String(options.days),
          limit: String(Math.min(100, Math.max(1, options.limit))),
        }
      )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (source === 'agent-llm') {
            this.agentAnalysisRuns = response.data ?? [];
            this.agentAnalysisRunCount =
              response.count ?? this.agentAnalysisRuns.length;
            this.agentAnalysisRunDays = response.days ?? options.days;
            this.agentRunsLoading = false;
          } else {
            this.voiceAnalysisRuns = response.data ?? [];
            this.voiceAnalysisRunCount =
              response.count ?? this.voiceAnalysisRuns.length;
            this.voiceAnalysisRunDays = response.days ?? options.days;
            this.voiceRunsLoading = false;
          }
        },
        error: (error: Error) => {
          if (source === 'agent-llm') {
            this.agentRunsError = this.errorMessage(error);
            this.agentRunsLoading = false;
          } else {
            this.voiceRunsError = this.errorMessage(error);
            this.voiceRunsLoading = false;
          }
        },
      });
  }

  loadEvaluation(source: EvaluationSource): void {
    const runDate =
      source === 'agent-llm' ? this.agentRunDate : this.voiceRunDate;
    if (!runDate) return;
    const runId = `${source}-weekly-${runDate}`;
    this.fetchEvaluationResults(source, runId);
  }

  // loads average of the average run scores
  loadAverageRunEvaluationScore(
    source: EvaluationSource,
    runs: EvaluationRun[]
  ): void {
    const completedRuns = runs.filter(
      (run) => run.status.trim().toUpperCase() === 'COMPLETED'
    );

    forkJoin(
      completedRuns.map((run) =>
        this.api
          .get<EvaluationResponse>(`admin/get-${source}-evaluation-results`, {
            runId: run.runId,
          })
          .pipe(
            take(1),
            map((response) => this.averageScore(response.data ?? [])),
            catchError(() => of(null))
          )
      )
    ).subscribe({
      next: (runAverages) => {
        const average = this.averageOfRunAverages(
          runAverages.filter((score) => score !== null)
        );

        const percentage = average !== null ? average * 100 : null;

        if (source === 'agent-llm') {
          this.agentAverageQualityScore = percentage;
        } else {
          this.voiceAverageQualityScore = percentage;
        }
      },
    });
  }

  private fetchEvaluationResults(
    source: EvaluationSource,
    runId: string
  ): void {
    const query: Record<string, string> = { runId };
    const group = source === 'agent-llm' ? this.agentGroup : this.voiceGroup;
    const metric = source === 'agent-llm' ? this.agentMetric : this.voiceMetric;
    if (group) query['group'] = group;
    if (metric) query['metric'] = metric;
    if (source === 'agent-llm') {
      this.agentLoading = true;
      this.agentError = '';
    } else {
      this.voiceLoading = true;
      this.voiceError = '';
    }
    this.api
      .get<EvaluationResponse>(`admin/get-${source}-evaluation-results`, query)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.setEvaluation(source, response.data ?? []);
        },
        error: (error: Error) => {
          this.setEvaluationError(source, this.errorMessage(error));
        },
      });
  }
  clearEvaluation(source: EvaluationSource): void {
    if (source === 'agent-llm') {
      this.agentGroup = '';
      this.agentMetric = '';
      this.agentResults = [];
    } else {
      this.voiceGroup = '';
      this.voiceMetric = '';
      this.voiceResults = [];
    }
  }
  applyCompliance(source: EvaluationSource, filters: ComplianceFilters): void {
    if (
      (filters.startDate && !filters.endDate) ||
      (!filters.startDate && filters.endDate)
    ) {
      this.setComplianceError(
        source,
        'Start and end dates must be supplied together.'
      );
      return;
    }
    this.loadCompliance(source, filters);
  }
  clearCompliance(source: EvaluationSource): void {
    const filters = { startDate: '', endDate: '', sk: '', limit: 50 };
    if (source === 'agent-llm') this.agentFilters = filters;
    else this.voiceFilters = filters;
    this.loadCompliance(source, filters);
  }
  refreshCompliance(source: EvaluationSource): void {
    this.loadCompliance(
      source,
      source === 'agent-llm' ? this.agentFilters : this.voiceFilters
    );
  }

  updateReviewStatus(source: EvaluationSource, item: ComplianceFinding): void {
    const key = this.reviewKey(item);
    this.updatingReviewKeys = new Set(this.updatingReviewKeys).add(key);
    const reviewStatus: ReviewStatus =
      item.reviewStatus === 'review-complete'
        ? 'needs-reviewing'
        : 'review-complete';
    const request: UpdateReviewStatusRequest = {
      source,
      pk: item.pk,
      sk: item.sk,
      reviewStatus,
    };
    this.api
      .post<UpdateReviewStatusResponse, UpdateReviewStatusRequest>(
        'admin/update-transcript-analysis',
        request
      )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.clearUpdatingReviewKey(key);
          this.applyReviewStatusUpdate(source, response.data);
        },
        error: (error: Error) => {
          this.clearUpdatingReviewKey(key);
          this.snackBar.open(
            `Failed to update review status: ${this.errorMessage(error)}`,
            'Dismiss',
            { duration: 6000 }
          );
        },
      });
  }

  private reviewKey(item: ComplianceFinding): string {
    return `${item.pk}#${item.sk}`;
  }

  private clearUpdatingReviewKey(key: string): void {
    const next = new Set(this.updatingReviewKeys);
    next.delete(key);
    this.updatingReviewKeys = next;
  }

  private applyReviewStatusUpdate(
    source: EvaluationSource,
    updated: ComplianceFinding
  ): void {
    const replace = (list: ComplianceFinding[]): ComplianceFinding[] =>
      list.map((finding) =>
        finding.pk === updated.pk && finding.sk === updated.sk
          ? { ...finding, ...updated }
          : finding
      );
    if (source === 'agent-llm') {
      this.agentFindings = replace(this.agentFindings);
      this.agentWeeklyAlerts = replace(this.agentWeeklyAlerts);
    } else {
      this.voiceFindings = replace(this.voiceFindings);
      this.voiceWeeklyAlerts = replace(this.voiceWeeklyAlerts);
    }
  }

  private loadCompliance(
    source: EvaluationSource,
    filters: ComplianceFilters
  ): void {
    const query: Record<string, string> = {
      source,
      limit: String(filters.limit),
    };
    if (filters.sk) query['sk'] = filters.sk;
    if (filters.startDate || filters.endDate) {
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      if (
        !filters.startDate ||
        !filters.endDate ||
        Number.isNaN(start.getTime()) ||
        Number.isNaN(end.getTime())
      ) {
        this.setComplianceError(
          source,
          'Please provide valid ISO date values for both dates.'
        );
        return;
      }
      query['startDate'] = start.toISOString();
      query['endDate'] = end.toISOString();
    }
    if (source === 'agent-llm') {
      this.agentFilters = filters;
      this.agentComplianceLoading = true;
      this.agentComplianceError = '';
    } else {
      this.voiceFilters = filters;
      this.voiceComplianceLoading = true;
      this.voiceComplianceError = '';
    }
    this.api
      .get<ComplianceResponse>('admin/get-transcript-analysis-alerts', query)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (source === 'agent-llm') {
            this.agentFindings = response.data ?? [];
            this.agentComplianceLoading = false;
          } else {
            this.voiceFindings = response.data ?? [];
            this.voiceComplianceLoading = false;
          }
        },
        error: (error: Error) =>
          this.setComplianceError(source, this.errorMessage(error)),
      });
  }
  private setEvaluation(
    source: EvaluationSource,
    results: EvaluationResult[]
  ): void {
    if (source === 'agent-llm') {
      this.agentResults = results;
      this.agentLoading = false;
    } else {
      this.voiceResults = results;
      this.voiceLoading = false;
    }
  }
  private setEvaluationError(source: EvaluationSource, message: string): void {
    if (source === 'agent-llm') {
      this.agentError = message;
      this.agentLoading = false;
    } else {
      this.voiceError = message;
      this.voiceLoading = false;
    }
  }
  private setComplianceError(source: EvaluationSource, message: string): void {
    if (source === 'agent-llm') {
      this.agentComplianceError = message;
      this.agentComplianceLoading = false;
    } else {
      this.voiceComplianceError = message;
      this.voiceComplianceLoading = false;
    }
  }
  private previousCompleteScheduledRunDate(now = new Date()): string {
    const date = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    );
    const scheduledDay = environment.evaluationScheduleDayUtc;
    const daysSinceScheduledDay = (date.getUTCDay() - scheduledDay + 7) % 7;
    const daysBack = daysSinceScheduledDay === 0 ? 7 : daysSinceScheduledDay;
    date.setUTCDate(date.getUTCDate() - daysBack);
    return date.toISOString().slice(0, 10);
  }
  private errorMessage(error: Error): string {
    return error.message || 'The API request failed. Please try again.';
  }

  backToDashboard() {
    this.router.navigate(['/dashboard']);
  }
}
